import { useState, useEffect, useMemo } from 'react'
import { useFarmBoostPairOptions } from 'hooks/useFarmBoostPairOptions'
import { ecoFarm } from 'config/constants/ecoBoost'
import {
  calcPotentialFarmBoostFactor,
  PotentialFarmBoostFactorResult,
} from 'state/farmBoost/calcPotentialFarmBoostFactor'
import { useFarmBoostFactor } from 'state/farmBoost/useFarmBoostFactor'
import { useUserBoostedFarmsInfo } from 'state/farmBoost/useUserBoostedFarmsInfos'
import { useDeserializedFarmFromPid } from 'state/farms/useDeserializedFarms'
import { getFarmVersionFromPid } from 'state/farms/farmsPidToVersion'
import { useFarmTokensPricesUsdc } from 'state/farms/useFarmTokensPricesUsdc'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import { getTotalBoostedApr } from 'views/Farms/utils'
import BigNumber from 'bignumber.js'
import { getFarmAprByVersion } from 'utils/apr'
import { getAddress } from 'utils/addressHelpers'
import { getBalanceAmount } from 'utils/formatBalance'
import { getLpUsdcPrice } from 'utils/farmsPriceHelpers'
import { useGetEcoBoostFactor } from 'views/Farms/hooks/useGetEcoBoostFactor'
import useGetNftStakeInfo from 'views/Farms/hooks/useGetNftStakeInfo'

const useGetBoostedAPRDetails = (farmId: number, targetBoostMoleIds: string[]) => {
  const { pairOptions } = useFarmBoostPairOptions()
  const targetFarmDetails = useDeserializedFarmFromPid(farmId, getFarmVersionFromPid(farmId))
  const targetFarmOption = pairOptions.find((option) => option.id === farmId)

  const actualBoostFactor = useFarmBoostFactor(farmId)
  const isActualBoostFactorLoading = !actualBoostFactor.loaded

  const [previewBoostFactorData, setPreviewBoostFactor] = useState<PotentialFarmBoostFactorResult>(null)
  const isPreviewBoostFactorDataLoading = previewBoostFactorData === null

  useEffect(() => {
    let latest = true
    setPreviewBoostFactor(null)
    calcPotentialFarmBoostFactor(farmId, {
      nfts: targetBoostMoleIds,
      excludeExistingNfts: true,
    }).then(async (calculatedBoostFactor) => {
      if (!latest) return

      setPreviewBoostFactor(calculatedBoostFactor)
    })

    return () => {
      latest = false
    }
  }, [farmId, targetBoostMoleIds])

  const [farmBoostUserInfo, isGetFarmUserInfoLoaded] = useUserBoostedFarmsInfo(farmId)
  const boostingNfts = farmBoostUserInfo?.boostingNfts ?? []

  const boostedAPRDetails = useMemo(() => {
    const originalPercentage =
      actualBoostFactor &&
      targetFarmOption &&
      parseFloat(
        getTotalBoostedApr(
          targetFarmOption.vvsRewardsApr,
          targetFarmOption.tokenRewardsApr,
          targetFarmOption.lpRewardsApr,
          actualBoostFactor?.boostFactor,
        ).toFixed(2),
      )
    const boostedPercentage =
      previewBoostFactorData &&
      targetFarmOption &&
      parseFloat(
        getTotalBoostedApr(
          targetFarmOption.vvsRewardsApr,
          targetFarmOption.tokenRewardsApr,
          targetFarmOption.lpRewardsApr,
          previewBoostFactorData?.boostFactor,
        ).toFixed(2),
      )
    return {
      primaryToken: targetFarmDetails?.token,
      secondaryToken: targetFarmDetails?.quoteToken,
      pairLabel: targetFarmDetails?.lpSymbol.split(' ')[0],
      originalPercentage,
      boostedPercentage,
      originStakedMinerMolesNumber: boostingNfts?.length,
      targetStakeMinerMolesNumber: targetBoostMoleIds?.length,
      originTotalBoostFactor: actualBoostFactor?.boostFactor,
      boostedTotalBoostFactor: previewBoostFactorData?.boostFactor,
      isLoading: isPreviewBoostFactorDataLoading && isActualBoostFactorLoading && !isGetFarmUserInfoLoaded,
    }
  }, [
    actualBoostFactor,
    targetFarmOption,
    previewBoostFactorData,
    targetFarmDetails?.token,
    targetFarmDetails?.quoteToken,
    targetFarmDetails?.lpSymbol,
    boostingNfts?.length,
    targetBoostMoleIds?.length,
    isPreviewBoostFactorDataLoading,
    isActualBoostFactorLoading,
    isGetFarmUserInfoLoaded,
  ])

  return boostedAPRDetails
}

export const useGetEcoFarmBoostedAPRDetails = (farmId: number, targetBoostNftIds: string[]) => {
  const tokenPrices = useFarmTokensPricesUsdc((s) => s.value)
  const targetFarmDetails = useDeserializedFarmFromPid(farmId, getFarmVersionFromPid(farmId))
  const ecoConfig = ecoFarm[targetFarmDetails?.ecoFarm]
  const boostPowerPerNfts = ecoConfig?.boostFactorPerNFT ?? 0

  const vvsPrice = usePriceVvsUsdc()
  const totalLiquidity = new BigNumber(targetFarmDetails.lpTotalInQuoteToken).times(
    targetFarmDetails.quoteTokenPriceUsdc,
  )
  const lpTokenPrice: BigNumber = getLpUsdcPrice(targetFarmDetails)
  const stakedLiquidityInUSD =
    targetFarmDetails.version === 2 && targetFarmDetails.migration
      ? getBalanceAmount(targetFarmDetails.lpTokenAmountInCraftsmanV2).times(lpTokenPrice)
      : totalLiquidity

  const {
    vvsRewardsApr: vapr,
    tokenRewardsApr: tapr,
    lpRewardsApr: lapr,
  } = getFarmAprByVersion(
    targetFarmDetails.poolWeight,
    vvsPrice,
    totalLiquidity,
    getAddress(targetFarmDetails.lpAddresses),
    targetFarmDetails.version,
    targetFarmDetails.rewardData,
    stakedLiquidityInUSD,
    tokenPrices,
  )

  const { data: actualBoostFactor, isLoading: isViewActualEcoBoostFactorLoading } = useGetEcoBoostFactor(farmId)

  const { boostingNfts, isLoading: isGetNftStakeInfoLoading } = useGetNftStakeInfo(farmId)

  const boostedAPRDetails = useMemo(() => {
    const boostedTotalBoostFactor = 1 + boostPowerPerNfts * targetBoostNftIds.length
    const originalPercentage = parseFloat(getTotalBoostedApr(vapr, tapr, lapr, 1, actualBoostFactor).toFixed(2))
    const boostedPercentage = parseFloat(getTotalBoostedApr(vapr, tapr, lapr, 1, boostedTotalBoostFactor).toFixed(2))
    return {
      primaryToken: targetFarmDetails?.token,
      secondaryToken: targetFarmDetails?.quoteToken,
      pairLabel: targetFarmDetails?.lpSymbol.split(' ')[0],
      originalPercentage,
      boostedPercentage,
      originStakedMinerMolesNumber: boostingNfts?.length,
      targetStakeMinerMolesNumber: targetBoostNftIds?.length,
      originTotalBoostFactor: actualBoostFactor,
      boostedTotalBoostFactor,
      isLoading: isViewActualEcoBoostFactorLoading || isGetNftStakeInfoLoading,
    }
  }, [
    boostPowerPerNfts,
    boostingNfts?.length,
    isGetNftStakeInfoLoading,
    isViewActualEcoBoostFactorLoading,
    vapr,
    lapr,
    tapr,
    targetBoostNftIds.length,
    targetFarmDetails?.lpSymbol,
    targetFarmDetails?.quoteToken,
    targetFarmDetails?.token,
    actualBoostFactor,
  ])

  return boostedAPRDetails
}

export default useGetBoostedAPRDetails
